/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import {
  ReportsSortFields,
  ReportStatus,
  RbacRoleBindingType,
  SortOrder,
  RoleBindingsByUsersSortFields,
} from "~/generated/graphql";
import { FilterButton, Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { reportsExcludeFilterMap } from "~/tools/filterStateMaps";
import { useGetRoleBindingsByUsers } from "~/views/permissions/Users/hooks/useGetRoleBindingsByUsers/useGetRoleBindingsByUsers";
import { useGetReports } from "~/views/resources/hooks";
import { ReportsQuery } from "~/views/resources/types";
import { prepareReportsFilter } from "./reportFilterBuilders";
import { reportTypeCheckboxes } from "./reportTypeLabels";

type State = {
  startDate?: string;
  endDate?: string;
  reportStatuses: string[];
  excludeReportStatuses: boolean;
  reportTypes: string[];
  excludeReportTypes: boolean;
  createdBy: string[];
  excludeCreatedBy: boolean;
};
type ReportsQueryState = ReportsQuery & {
  page?: string | number;
  limit?: string | number;
};

const reportStatusCheckboxes = [
  {
    value: ReportStatus.InProgress,
    label: "In Progress",
    subLabel: "The report is currently being generated",
  },
  {
    value: ReportStatus.Completed,
    label: "Completed",
    subLabel:
      "The report has been successfully generated and is ready for download",
  },
  {
    value: ReportStatus.Failed,
    label: "Failed",
    subLabel: "The report generation was unsuccessful",
  },
];

type FilterSectionProps = {
  name: string;
  isSelected: boolean;
  selectedOptions: string[];
  selectableItems: Array<{ value: string; label: string; subLabel?: string }>;
  isExcludeSelected: boolean;
  onClear: () => void;
  onMultiSelect: (values: string[]) => void;
  onModeChange: (mode: Mode) => void;
};

function FilterSection({
  name,
  isSelected,
  selectedOptions,
  selectableItems,
  isExcludeSelected,
  onClear,
  onMultiSelect,
  onModeChange,
}: FilterSectionProps) {
  return (
    <FilterWrapper
      name={name}
      isSelected={isSelected}
      selectedOptions={selectedOptions}
      onClear={onClear}
    >
      <SelectableListWithSearch
        allowModeChange
        selectedOptions={selectedOptions}
        selectableItems={selectableItems}
        isExcludeSelected={isExcludeSelected}
        onMultiSelect={onMultiSelect}
        onModeChange={onModeChange}
      />
    </FilterWrapper>
  );
}

function buildState(queryState: ReportsQuery) {
  return {
    startDate: queryState.dateGeneratedStart,
    endDate: queryState.dateGeneratedEnd,
    reportStatuses: queryState.reportStatuses ?? [],
    excludeReportStatuses: queryState.excludeReportStatuses ?? false,
    reportTypes: queryState.reportTypes ?? [],
    excludeReportTypes: queryState.excludeReportTypes ?? false,
    createdBy: queryState.createdBy ?? [],
    excludeCreatedBy: queryState.excludeCreatedBy ?? false,
  };
}

function buildQueryStateFromSidebarState(
  queryState: ReportsQueryState,
  state: State
): ReportsQueryState {
  return {
    ...queryState,
    reportStatuses:
      state.reportStatuses.length > 0 ? state.reportStatuses : undefined,
    excludeReportStatuses: state.excludeReportStatuses || undefined,
    reportTypes: state.reportTypes.length > 0 ? state.reportTypes : undefined,
    excludeReportTypes: state.excludeReportTypes || undefined,
    createdBy: state.createdBy.length > 0 ? state.createdBy : undefined,
    excludeCreatedBy: state.excludeCreatedBy || undefined,
    dateGeneratedStart: state.startDate
      ? dayjs(state.startDate).format(dateFormat.fullDate)
      : undefined,
    dateGeneratedEnd: state.endDate
      ? dayjs(state.endDate).endOf("day").format(dateFormat.fullDate)
      : undefined,
  };
}

export function ReportsFiltersSidebar({
  resultsCount = 0,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<ReportsQuery>();

  const { data: fetchedData } = useGetRoleBindingsByUsers({
    page: "1",
    limit: "1000",
    sort: {
      field: RoleBindingsByUsersSortFields.Subject,
      order: SortOrder.Asc,
    },
  });

  const users = fetchedData
    .filter(isNotNil)
    .filter((user) => user.type === RbacRoleBindingType.User);

  const createdByCheckboxes = users.map((user) => {
    return {
      value: user.subject,
      label: user.subject,
    };
  });

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    reportsExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );
  const { count: previewCount, loading: previewLoading } = useGetReports({
    page: String(draftQueryState.page ?? "1"),
    limit: String(draftQueryState.limit ?? "20"),
    sort: {
      field: draftQueryState.field ?? ReportsSortFields.DateGenerated,
      order: draftQueryState.order ?? SortOrder.Desc,
    },
    filter: prepareReportsFilter(draftQueryState),
    skip: !isOpen || !hasPendingChanges,
  });
  const previewResultsCount = previewLoading ? undefined : previewCount;
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "reports",
  });

  const clearDateGeneratedFilter = () => {
    updateState({
      startDate: undefined,
      endDate: undefined,
    });
  };

  const clearReportStatusFilter = () => {
    updateState({
      reportStatuses: [],
      excludeReportStatuses: false,
    });
  };

  const clearReportTypeFilter = () => {
    updateState({
      reportTypes: [],
      excludeReportTypes: false,
    });
  };

  const clearCreatedByFilter = () => {
    updateState({
      createdBy: [],
      excludeCreatedBy: false,
    });
  };

  const clearAllFilters = () => {
    updateState({
      startDate: undefined,
      endDate: undefined,
      reportStatuses: [],
      excludeReportStatuses: false,
      reportTypes: [],
      excludeReportTypes: false,
      createdBy: [],
      excludeCreatedBy: false,
    });
    updateQueryState({
      ...queryState,
      reportStatuses: undefined,
      excludeReportStatuses: undefined,
      reportTypes: undefined,
      excludeReportTypes: undefined,
      createdBy: undefined,
      excludeCreatedBy: undefined,
      dateGeneratedStart: undefined,
      dateGeneratedEnd: undefined,
      page: "1",
    });
    close();
  };

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);

    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });
    close();
  };

  const isDateGeneratedSelected =
    isNotNil(state.startDate) && isNotNil(state.endDate);
  const isReportStatusSelected =
    isNotNil(state.reportStatuses) && isNotEmpty(state.reportStatuses);
  const isReportTypeSelected =
    isNotNil(state.reportTypes) && isNotEmpty(state.reportTypes);
  const isCreatedBySelected =
    isNotNil(state.createdBy) && isNotEmpty(state.createdBy);
  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "reports",
    excludeFilterMap: reportsExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  const handleDateGeneratedRangeSelect = (range: {
    startDate?: string;
    endDate?: string;
  }) => {
    updateState(range);
  };

  const handleReportStatusMultiSelect = (statuses: string[]) => {
    updateState({ reportStatuses: statuses });
  };

  const handleReportStatusModeChange = (mode: Mode) => {
    updateState({ excludeReportStatuses: mode === Mode.EXCLUDES });
  };

  const handleReportTypeMultiSelect = (types: string[]) => {
    updateState({ reportTypes: types });
  };

  const handleReportTypeModeChange = (mode: Mode) => {
    updateState({ excludeReportTypes: mode === Mode.EXCLUDES });
  };

  const handleCreatedByMultiSelect = (createdBy: string[]) => {
    updateState({ createdBy });
  };

  const handleCreatedByModeChange = (mode: Mode) => {
    updateState({ excludeCreatedBy: mode === Mode.EXCLUDES });
  };

  return (
    <>
      <FilterButton onClick={open}>Filters</FilterButton>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Date Generated"
            isSelected={isDateGeneratedSelected}
            selectedOptions={isDateGeneratedSelected ? ["dateRange"] : []}
            onClear={clearDateGeneratedFilter}
          >
            <SelectableDateRange
              selectedRange={{
                startDate: state.startDate,
                endDate: state.endDate,
              }}
              onRangeSelect={handleDateGeneratedRangeSelect}
              label="Time range when report was generated"
              setDefaultEndDateToToday
            />
          </FilterWrapper>
          <FilterSection
            name="Report Status"
            isSelected={isReportStatusSelected}
            selectedOptions={state.reportStatuses}
            selectableItems={reportStatusCheckboxes}
            isExcludeSelected={state.excludeReportStatuses}
            onClear={clearReportStatusFilter}
            onMultiSelect={handleReportStatusMultiSelect}
            onModeChange={handleReportStatusModeChange}
          />
          <FilterSection
            name="Report Type"
            isSelected={isReportTypeSelected}
            selectedOptions={state.reportTypes}
            selectableItems={reportTypeCheckboxes}
            isExcludeSelected={state.excludeReportTypes}
            onClear={clearReportTypeFilter}
            onMultiSelect={handleReportTypeMultiSelect}
            onModeChange={handleReportTypeModeChange}
          />
          <FilterSection
            name="Created by"
            isSelected={isCreatedBySelected}
            selectedOptions={state.createdBy}
            selectableItems={createdByCheckboxes}
            isExcludeSelected={state.excludeCreatedBy}
            onClear={clearCreatedByFilter}
            onMultiSelect={handleCreatedByMultiSelect}
            onModeChange={handleCreatedByModeChange}
          />
        </Sidebar>
      </When>
    </>
  );
}
